import { useState, useCallback, useMemo } from 'react'
import { StepResponseMap } from './types'

export interface DocumentUploadState {
  isLoading: boolean
  error: string | null
  success: boolean
  progress: number
}

const defaultDocumentState: DocumentUploadState = {
  isLoading: false,
  error: null,
  success: false,
  progress: 0
}

export const useDocumentStates = () => {
  const [documentStates, setDocumentStates] = useState<Record<keyof StepResponseMap, DocumentUploadState>>({
    prescription: { ...defaultDocumentState },
    pill_prescription: { ...defaultDocumentState },
    address: { ...defaultDocumentState },
    identity: { ...defaultDocumentState },
    affiliation: { ...defaultDocumentState },
    anvisa: { ...defaultDocumentState },
    laudo_medico: { ...defaultDocumentState },
    powerOfAttorney: { ...defaultDocumentState },
    responsibleIdentity: { ...defaultDocumentState }
  })

  const updateDocumentState = useCallback((stepName: keyof StepResponseMap, updates: Partial<DocumentUploadState>) => {
    setDocumentStates(prev => ({
      ...prev,
      [stepName]: { ...prev[stepName], ...updates }
    }))
  }, [])

  const getDocumentState = useCallback((stepName: keyof StepResponseMap): DocumentUploadState => {
    return documentStates[stepName] || defaultDocumentState
  }, [documentStates])

  const isDocumentSuccess = useCallback((stepName: keyof StepResponseMap): boolean => {
    return documentStates[stepName]?.success || false
  }, [documentStates])

  const isDocumentLoading = useCallback((stepName: keyof StepResponseMap): boolean => {
    return documentStates[stepName]?.isLoading || false
  }, [documentStates])

  const getDocumentError = useCallback((stepName: keyof StepResponseMap): string | null => {
    return documentStates[stepName]?.error || null
  }, [documentStates])

  const resetDocumentState = useCallback((stepName: keyof StepResponseMap) => {
    updateDocumentState(stepName, { ...defaultDocumentState })
  }, [updateDocumentState])

  const resetAllStates = useCallback(() => {
    setDocumentStates({
      prescription: { ...defaultDocumentState },
      pill_prescription: { ...defaultDocumentState },
      address: { ...defaultDocumentState },
      identity: { ...defaultDocumentState },
      affiliation: { ...defaultDocumentState },
      anvisa: { ...defaultDocumentState },
      laudo_medico: { ...defaultDocumentState },
      powerOfAttorney: { ...defaultDocumentState },
      responsibleIdentity: { ...defaultDocumentState }
    })
  }, [])

  const computedStates = useMemo(() => ({
    receitaSuccess: isDocumentSuccess('prescription'),
    pillPrescriptionSuccess: isDocumentSuccess('pill_prescription'),
    addressSuccess: isDocumentSuccess('address'),
    identitySuccess: isDocumentSuccess('identity'),
    relationshipSuccess: isDocumentSuccess('affiliation'),
    anvisaSuccess: isDocumentSuccess('anvisa'),
    laudoMedicoSuccess: isDocumentSuccess('laudo_medico'),

    isGlobalLoading: Object.values(documentStates).some(state => state.isLoading),

    hasAnySuccess: Object.values(documentStates).some(state => state.success),

    globalErrors: Object.values(documentStates)
      .map(state => state.error)
      .filter(error => error !== null)
  }), [documentStates, isDocumentSuccess])

  return {
    documentStates,
    updateDocumentState,
    resetDocumentState,
    resetAllStates,
    getDocumentState,
    isDocumentSuccess,
    isDocumentLoading,
    getDocumentError,
    ...computedStates
  }
}

export const useUploadDisplay = (uploadState: DocumentUploadState, currentFile: File | null) => {
  const shouldShowProgress = useMemo(() => {
    return currentFile !== null && (
      uploadState.isLoading || 
      uploadState.error !== null || 
      uploadState.success
    )
  }, [currentFile, uploadState])

  const shouldClearFile = useMemo(() => {
    return uploadState.success && currentFile
  }, [uploadState.success, currentFile])

  const displayStatus = useMemo(() => {
    if (uploadState.isLoading) return 'uploading'
    if (uploadState.success) return 'success'
    if (uploadState.error) return 'error'
    return 'idle'
  }, [uploadState])

  return {
    shouldShowProgress,
    shouldClearFile,
    displayStatus: displayStatus as 'idle' | 'uploading' | 'success' | 'error'
  }
}
